import { statementPayoutMatchingAPI } from '../../lib/api';
import { MatchRepairPanel } from './MatchRepairPanel';

// Repairs statement_payout_matches confirmed at the match-doc level whose
// transaction was never (re-)stamped reconciled (repairable), and surfaces
// two independent dead-side cases:
//   - orphaned_transaction: transaction_id no longer resolves at all (e.g.
//     hard-deleted by a mock-data/bank-account purge).
//   - orphaned_statement: transaction still resolves, but statement_id no
//     longer resolves to a LIVE (non-excluded) management_statements doc --
//     hard-deleted, or soft-excluded as a duplicate. Repairing this in
//     place would stamp the transaction against a statement that no longer
//     exists; release unstamps the transaction instead.
// Same repairable bug class as RecurringCostMatchRepairPanel: the historical
// revolut/wise/bridge bank resync wrote "is_reconciled": False inside its
// $set on every run, silently reverting a confirmed match's transaction-side
// stamp for as long as those writers ran that way (prospectively fixed, but
// not retroactively -- see
// services/statement_payout_matcher.py::classify_confirmed_matches).
// Thin config wrapper over the shared MatchRepairPanel -- kept as explicit
// local functions (not a bare object reference) so the API calls stay
// visible by name here, same live-wiring contract
// recurringCostMatchRepairPanel.test.js established for its sibling panel.
const repairPreview = () => statementPayoutMatchingAPI.repairPreview();
const repairApply = () => statementPayoutMatchingAPI.repairApply();
const repairRelease = (matchId) => statementPayoutMatchingAPI.repairRelease(matchId);

const REPAIRABLE_COLUMNS = [
  { key: 'id', label: 'Match ID' },
  { key: 'statement_id', label: 'Statement ID' },
  { key: 'transaction_id', label: 'Transaction ID' },
  { key: 'confirm_step', label: 'Confirm step', className: 'px-3 py-2 tabular-nums text-xs' },
];

// mis_released rows are matches release_orphaned_match already touched
// under the pre-terminal-status code path and left stuck at
// status="suggested" against a dead statement or transaction (prod: match
// 7b87b946) -- classify_confirmed_matches surfaces them here (not just
// genuinely orphaned status="confirmed" rows) since this is the only card
// with a Release action; Check Orphaned Links can find them but has none.
const MIS_RELEASED_NOTE_COLUMN = {
  key: 'mis_released',
  label: 'Note',
  className: 'px-3 py-2 text-xs font-medium text-amber-600 dark:text-amber-400',
  render: (m) => (m.mis_released ? 'Mis-released (stuck at "suggested")' : ''),
};

const ORPHANED_TRANSACTION_COLUMNS = [
  { key: 'id', label: 'Match ID' },
  { key: 'statement_id', label: 'Statement ID' },
  { key: 'transaction_id', label: 'Dead transaction ID' },
  MIS_RELEASED_NOTE_COLUMN,
];

const ORPHANED_STATEMENT_COLUMNS = [
  { key: 'id', label: 'Match ID' },
  { key: 'statement_id', label: 'Dead statement ID' },
  { key: 'transaction_id', label: 'Transaction ID' },
  MIS_RELEASED_NOTE_COLUMN,
];

const RELEASE_BUCKETS = [
  {
    key: 'orphaned_transaction',
    summaryLabel: 'orphaned (transaction)',
    caption: 'Orphaned transaction (transaction no longer exists -- release to reopen the statement for a fresh match)',
    columns: ORPHANED_TRANSACTION_COLUMNS,
  },
  {
    key: 'orphaned_statement',
    summaryLabel: 'orphaned (statement)',
    caption: 'Orphaned statement (statement no longer exists or is excluded as a duplicate -- release to unstamp the transaction)',
    columns: ORPHANED_STATEMENT_COLUMNS,
  },
];

export function StatementPayoutMatchRepairPanel() {
  return (
    <MatchRepairPanel
      title="Statement Payout Match Repair"
      description="Finds statement_payout_matches confirmed at the match-doc level whose
          transaction was never (re-)stamped reconciled (repairable), whose
          transaction no longer exists at all (orphaned transaction), or
          whose statement no longer exists / is excluded as a duplicate
          while the transaction is still live (orphaned statement), and
          completes/releases them via the same idempotent confirm helper
          every fresh statement-payout apply uses. Never touches the
          statement's own Paid/Reconciled stamp in the orphaned-transaction
          case; unstamps the transaction instead in the orphaned-statement
          case. Also surfaces mis-released matches: a match Release already
          touched under the pre-terminal-status code path, left stuck at
          status='suggested' against a dead statement or transaction --
          marked 'Mis-released' in the table below, same Release action
          finishes the terminal transition."
      repairPreview={repairPreview}
      repairApply={repairApply}
      repairRelease={repairRelease}
      repairableColumns={REPAIRABLE_COLUMNS}
      releaseBuckets={RELEASE_BUCKETS}
    />
  );
}
